/**
 * WebSocket wire protocol between client and server.
 *
 * JSON for Phase 2 simplicity; the message shapes are kept flat and numeric
 * so a later swap to a binary codec (per phase-6) is mechanical. Positions
 * are absolute system-space coordinates (doubles survive JSON fine); the
 * client converts to floating-origin local space on receive.
 */

import type { ShipLayout } from "./ships.js";
import type { CharacterStyle, EquipSlot } from "./rpg.js";
import type { QuestInstance } from "./quests/types.js";
import type { StoryStateV1 } from "./story/state.js";
import type { ChatChannel } from "./social.js";
import type { SystemRevealV1 } from "./hiddenUniverse.js";

// v36: THE PARRY (Elden Ring model) — PlayerState.py (parry window: server staggers melee
// attackers + deflects bolts), DroneAnim.st (stagger posture), DroneHitMsg.a (per-action
// damage-cap validation for the riposte), sword_parry/sword_riposte action ids.
// v37: chat history (chatHist) + friends (social/socialState). v38: parties
// (party/partyState). v39: GUILDS (social phase 4) — GuildMsg ops +
// GuildStateMsg (roster with roles/presence, pending invite), plus
// RemotePlayer.tag + `ptag` for the `[TAG] Name` badge over a player's head.
// v40: CACHES phase 3 (instanced dungeons) — cacheEnter/cacheLeave (client) +
// cacheState (server), plus the `cache:<site>:<key>:<epoch>` system-id kind.
// v41: MULTI-CHARACTER accounts — JoinMsg.character selects which of the
// account's characters (users/{uid}/characters/{cid}) this session plays;
// home-system ids gain the per-character `home:<uid>.<cid>` form.
// v42: SETTLEMENT WORK ORDERS — owner-only settlement state plus bootstrap,
// storage transfer, commission queue, event acknowledgement, and decommission
// intents. Production remains timestamp-integrated and server-authoritative.
// v43: SETTLEMENT PROGRESSION — completed work grants resident XP; explicit
// facility-upgrade and resident-promotion intents create deterministic rate
// boundaries and spend only server-authoritative settlement goods.
// v44: SETTLEMENT RECRUITMENT — deterministic owner-scoped applicant pools
// and transactional recruit intents with server-derived bed/workstation binds.
// v45: SETTLEMENT GROWTH — persistent stage/lifetime accomplishments and an
// explicit, server-authoritative Camp → Homestead advancement intent.
// v46: APPLICANT ASSIGNMENT — bounded server-derived bed/workstation choices;
// recruit intents may select one exact displayed placement.
// v47: SETTLEMENT RELOCATION — resident bed/station reassignment and stable
// facility relicensing after furniture or room topology changes.
// v48: SIX-FAMILY SETTLEMENT ECONOMY — capability-neutral building families,
// worker specialties, graded goods, bounded production targets, and Ari/Mara
// two-friend founding.
// v49: EQUIPMENT SLOTS, GARAGE, WARDROBE — worn gear leaves the bag, vehicles are
// claimed once into a per-character garage, and appearance is gated on the collection
// ledger (CharacterRecordV1.unlocks) rather than trusted from the client.
// v50: PERSONAL SHIELDS — a server-owned Halo-style shield pool over hp (armor's
// `shield` stat), streamed on HealthMsg; `shdraw` converts shield into action
// energy at a loss.
// v51: local terrain relief changes the shared physical world. Reject clients
// with the previous sampler rather than allowing mismatched ground/collision.
// v52: shared coastal glacial geology changes solid terrain on ice planets.
// v53: finite volcanic geology and shared lava contact boundaries.
// v54: deterministic eruption timing and localized, telegraphed crater heat.
// v55: shared mountain masses replace thin ridged walls and change ground support.
// v56: connected regional ranges/valleys change shared terrain and drainage.
// v57: selected coastal reaches reshape shared beaches, cliffs and seabeds.
// v58: classified coastal reaches and shared terminal estuary support.
// v59: shared sea-stack terrain changes coastal support and water depths.
// v60: shared plateau escarpments change physical support and drainage.
// v61: deterministic dune provinces change physical ground and drainage.
// v62: native sandstone buttes change physical ground and drainage.
// v63: connected canyon floors and their validated stream routes.
// v64: fractured canyon heightfields, with preserved floors and stream routes.
// v65: finite 3D canyon shelves and shared ceiling/support/contact queries.
// v68: continuous river bend surfaces change shared water and bank support.
// v69: native cliff lips and separate lower waterfall receivers change support.
// v70: retained waterfall-bank rock changes shared dry terrain support.
// v71: shared cascade escarpments alter terrain and local river surfaces.
// v72: streamed frontier buildings and current compact programs share new
// doors, room floors, roof and wall collision. Old geometry cannot join it.
export const PROTOCOL_VERSION = 72;

/** Server tick rate for state broadcast (Hz). */
export const SERVER_BROADCAST_HZ = 10;
/** Client input/state send rate (Hz). */
export const CLIENT_SEND_HZ = 10;
/** Remote players render this many ms in the past for interpolation. */
export const INTERP_DELAY_MS = 150;

/**
 * Phase-1 combat authority switch. When ON, on-foot/ship clients send fire INTENTS
 * (not hit claims) plus an instant predicted-visual bolt, and the server simulates the
 * authoritative bolt in the target body's LOCAL frame, lag-compensated, and broadcasts
 * the result (`bhit`). When OFF, the legacy client-authoritative relay + `dhit` claim
 * path runs. Flipping this is the rollback.
 */
export const SERVER_AUTHORITATIVE_BOLTS = true;
/**
 * Hard ceiling (ms) on how far a shot may lag-compensate (rewind targets into the past),
 * regardless of the client-supplied view time — the anti-cheat clamp. The legitimate
 * rewind is ~ one-way-latency + INTERP_DELAY_MS; this bounds a forged `ct`.
 */
export const MAX_REWIND_MS = 500;

/**
 * Phase-2 authority switch (independent of bolts). When ON, the SERVER owns each player's HP:
 * it applies bolt-vs-player, drone-vs-player, and reported self (fall/hazard) damage, streams
 * authoritative `hp`, and owns death/respawn — the client stops self-applying damage and just
 * follows the streamed HP (still shows predicted hit FX). When OFF, the legacy client-owned
 * relay HP runs. Flipping this is the rollback.
 */
export const SERVER_AUTHORITATIVE_HP = true;
/**
 * Player hit-sphere radius (m), centered at the streamed feet position. MATCHES the client's
 * remote-character bolt collider so a shooter's prediction and the server's authoritative sim
 * agree on what counts as a hit.
 */
export const PLAYER_HIT_RADIUS = 1.6;
/** Favor-the-shooter tolerance (m) on the player hurt volume (smaller than the drone one —
 *  players render buffered, so the server reconstructs their shown position almost exactly).
 *  A property of LAG COMP, not of the body's shape, so it rides along on whichever volume is
 *  tested: the bolt sphere (PLAYER_HIT_RADIUS) or the melee capsule (MELEE_HIT_RADIUS). */
export const PLAYER_HIT_MARGIN = 0.25;
/** MELEE hurt-capsule height (m) — model C: one feet-to-head body capsule per character. `lp` is
 *  the character's ground contact, so the capsule runs from it straight up. */
export const PLAYER_HIT_HEIGHT = 1.8;
/**
 * MELEE hurt-capsule RADIUS (m) — a human torso, half-width.
 *
 * DO NOT "unify" THIS WITH PLAYER_HIT_RADIUS. They are different shapes' radii and the numbers are
 * not interchangeable:
 *   • PLAYER_HIT_RADIUS (1.6, above) is the radius of a SPHERE centred on the body — the bolt
 *     hitbox (server/projectiles.ts). A single 1.6 m ball already swallows the whole character,
 *     head to feet, which is why it is that big. It is correct there. Leave it alone.
 *   • MELEE_HIT_RADIUS (this) is the radius of a 1.8 m FEET-TO-HEAD CAPSULE. The capsule's CORE
 *     already spans the body vertically, so its radius only has to cover the body's HALF-WIDTH.
 * Feeding the sphere radius into the capsule therefore counts the body twice, and it showed:
 * with 1.6 + 0.25 the sword's measured envelope was ~3.4 m forward, 3.5 m BEHIND and 3.0 m
 * sideways — a swing that killed people standing three and a half metres behind your back.
 * At 0.75 the blade reaches roughly a blade's length, which is the entire point of baking an arc.
 *
 * (The blade DOES legitimately sweep behind the attacker — sword_light_1 is a 220°+ overhead chop
 * — so a victim hugging your back can still be caught. That is the swing, not the capsule.)
 *
 * SHARED, not server-local: the victim's client predicts a relayed melee swing against this exact
 * capsule (main.ts) and the server resolves it against the same one (rooms.applyMelee). Two copies
 * of these three numbers is two hurt volumes, and the difference shows up as HP rubber-banding.
 */
export const MELEE_HIT_RADIUS = 0.75;

/**
 * Phase-3 transport switch: the UNRELIABLE channel (geckos.io WebRTC datachannels over UDP)
 * for the per-tick `states`/`drones` snapshots, where the newest packet supersedes the last —
 * drop-don't-delay, no TCP head-of-line blocking. CLIENT master switch; the SERVER additionally
 * requires env `RT_UDP=1` (set on the UDP-capable GCE VM, never on Cloud Run, which has no UDP
 * ingress). Without the server env the welcome carries no `rt` token and clients never attempt
 * the channel. Everything else (events, hits, inventory) stays on the reliable WebSocket, and
 * snapshots fall back to it automatically whenever the UDP channel is absent or drops.
 */
export const TRANSPORT_UNRELIABLE = true;

/**
 * Phase-4: render DRONES on the same buffered timeline as remote players (sampled at
 * serverNow − INTERP_DELAY_MS from a per-drone snapshot ring) instead of easing/extrapolating
 * toward the newest snapshot. Kills the velocity-extrapolation error on direction changes, so
 * server-side lag comp reconstructs EXACTLY what the shooter saw — the drone rewind becomes the
 * full `rawRewind` (same as players) and the favor-the-shooter margin tightens. Read by BOTH the
 * client renderer and the server projectile sim — they must agree, which is why it lives here.
 * Cost: drone motion displays ~INTERP_DELAY_MS in the past (as remote players already do).
 */
export const DRONE_INTERP_BUFFERED = true;

export interface PlayerState {
  /** Position in system space (character feet, ship, or driven vehicle). */
  p: [number, number, number];
  /** Orientation quaternion [x, y, z, w]. */
  q: [number, number, number, number];
  /** Velocity in system space (units/s) for extrapolation. */
  v: [number, number, number];
  /** How to render this player; omitted means "ship" (v3 compatibility). */
  k?: "ship" | "foot" | "vehicle";
  /** Ground-vehicle definition id while `k === "vehicle"`. */
  veh?: string;
  /** Normalized boost reserve, 0..1, for remote FX/HUD. */
  vb?: number;
  /** Parked ship pose while on foot (system space). */
  sp?: [number, number, number];
  sq?: [number, number, number, number];
  /** Held weapon item id while on foot (remote clients render + aim it). */
  w?: string;
  /** STOWED weapon item id (equipped, holstered on the back) — remotes render it there. */
  sw?: string;
  /**
   * Planet-LOCAL anchoring (v9, on foot): index into system.planets plus
   * the character's local pose in that planet's rotating frame. SENDER-
   * computed — the sender's ground contact is exact in its own frame, so
   * receivers re-derive world from their RENDERED pose with zero clock
   * involvement (receive-side time reconstruction drifted with any sender
   * clock lag).
   */
  pl?: number;
  lp?: [number, number, number];
  lr?: [number, number, number, number];
  /** Same local anchoring for the parked/autopiloting hull. */
  slp?: [number, number, number];
  slr?: [number, number, number, number];
  /**
   * Docked at the system station, pad index (v10): the pose is DERIVED by
   * every receiver from the shared station math — nothing to interpolate.
   */
  dock?: number;
  // --- v15 ON-FOOT ANIMATION STATE (so remotes render flight/aim, not just ground) ---
  /** Wings: 1 = hover, 2 = glide, 3 = dive. Omitted/0 = none. */
  j?: number;
  /** Crystal engine effort, 0..1; absent means unpowered flight. */
  fp?: number;
  /** Seconds left in flight-launch windup plus free burst, currently 0..0.7. */
  fl?: number;
  /** Ctrl dive preparation progress, 0..1; omitted when not preparing. */
  dp?: number;
  /** Unpowered landing anticipation (0–1). */
  fa?: number;
  /** Signed wall phase: negative left, positive right; 1 catch, 2 run, 3 jump. */
  wr?: number;
  /** Seconds into the wall phase, for late/remote animation entry. */
  wt?: number;
  /** Ledge assist: 1 low step, 2 reaching climb; age in seconds and body-local lip. */
  lc?: number;
  lt?: number;
  le?: [number, number, number];
  /** Aiming down sights. */
  am?: boolean;
  /** Airborne (NOT grounded). Omitted = grounded. */
  air?: boolean;
  /** Crouching. */
  cr?: boolean;
  /** Body-local move direction + climb — drives the directional/strafe/climb flight clips. */
  mf?: number;
  mr?: number;
  cl?: number;
  /** Aim pitch (radians) for the torso aim solve while ADS. */
  pt?: number;
  /** Hard-landing stagger active (mirrors the landing stagger to remotes). */
  ld?: boolean;
  /** A RAISED SHIELD GUARD (RMB in shield mode). The server soaks GUARD_SOAK of any damage
   *  the holder takes; the holder bills the soaked part to their own energy. */
  bl?: boolean;
  /** Shield MODE with energy left — passive absorption (SHIELD_SOAK). Same trust model as
   *  `bl`/`iv`: the client asserts it, the server mitigates from it, the client pays for it. */
  sh?: boolean;
  /** Invulnerable to incoming damage right now — a dodge i-frame or parry window. The server
   *  reads this (at the lag-comp'd hit time) to negate a hit under SERVER_AUTHORITATIVE_HP —
   *  mitigation it can't time itself. Transient + self-limiting on the client. */
  iv?: boolean;
  /** The PARRY WINDOW is live (rides alongside `iv`, which still negates). The server reacts:
   *  an enemy MELEE strike landing in it STAGGERS the attacker (server-owned stun state), a
   *  BOLT landing in it is DEFLECTED back along its path. Same trust model as `iv` — the
   *  client times it, the window is short (150 ms) and self-limiting, the server acts on it
   *  only at a validated hit's lag-comp'd landing tick. */
  py?: boolean;
  /** Submersion 0..1 while in water — remotes show the swim pose. Relay only; derived
   *  from the deterministic seaRadius, so the water surface stays non-authoritative. */
  sub?: number;
}

// --- Client → Server ---

export interface JoinMsg {
  t: "join";
  v: number; // protocol version
  name: string;
  /** Star system id ("x:y:z") — the server rooms players by this. */
  system: string;
  state: PlayerState;
  /** Modular ship layout; server validates and falls back to the default. */
  ship: ShipLayout;
  /** Character appearance; server validates, default style on failure. */
  style?: CharacterStyle;
  /** Firebase ID token; verified server-side when auth is configured. */
  auth?: string;
  /** Which of the account's characters this session plays (`c<slot>`). The
   *  server resolves uid+character to the authoritative record; identity and
   *  appearance still come from that record, never from this payload. */
  character?: string;
  /** Short-lived admission ticket returned with a sealed-system reveal. */
  access?: string;
}

export interface StateMsg {
  t: "state";
  state: PlayerState;
}

/** Client rebuilt their ship in the shipyard. */
export interface RefitMsg {
  t: "refit";
  ship: ShipLayout;
}

/** Client restyled their character (suit locker). */
export interface StyleMsg {
  t: "style";
  style: CharacterStyle;
}

/**
 * Client fired a weapon group. Doubles as the relay (others render the muzzle flash)
 * AND, when SERVER_AUTHORITATIVE_BOLTS is on, the fire INTENT the server simulates from.
 * The v17 intent fields are optional so a bare `{g,d}` still relays (back-compat); the
 * server reads weapon numbers from `wp` (never a client-sent damage).
 */
export interface FireMsg {
  t: "fire";
  g: 1 | 2;
  /**
   * Aim direction (unit, system space) for ON-FOOT shots — a ship's mounts
   * define their own directions, but a character's vertical aim can't be
   * reconstructed from the streamed pose.
   */
  d?: [number, number, number];
  // --- v17 fire intent (server-authoritative bolts) ---
  /** Muzzle origin, system space (server validates it's near the shooter). */
  o?: [number, number, number];
  /** Planet anchor index the shot resolves in; -1 / omitted → system frame. */
  pl?: number;
  /** Client fire sequence (monotonic) for dedupe + visual reconciliation. */
  seq?: number;
  /** Client sim-time it aimed at (server-clock ms) — the lag-comp rewind target. */
  ct?: number;
  /** Weapon item id; the server reads projectileSpeed/damage from the def. */
  wp?: string;
  /** Vehicle hardpoint + weapon ids; both are validated against state.veh. */
  vh?: string;
  vw?: string;
}

/** Client claims a hit on a drone; the SERVER owns drone health. */
export interface DroneHitMsg {
  t: "dhit";
  id: number;
  dmg: number;
  /** LAUNCH claim (m/s radial impulse — the DMC vertical game): the striking action's
   *  `launch` (COMBAT_ACTIONS; launcher pops, aerials re-lift/juggle). The server CAPS it
   *  and applies only to groundable kinds (never flyers) — see drones.ts hit(). */
  up?: number;
  /** The CombatActionId that dealt this hit. The server caps `dmg` from the ACTION's table
   *  damage (× the max upgrade multiplier) instead of the flat blade cap — how the riposte's
   *  3.5× claim clears validation without widening the cheat ceiling for ordinary swings. */
  a?: string;
  /** BOLT claim (2026-08-02, mixed authority): the weapon item id that fired it. The server
   *  derives the damage cap (the def's damage; ×2 for the upgradeable cannon construct) and
   *  the claim range (the def's range + lag slack) from the catalog row. */
  w?: string;
}

/** A PARRY knockback claim — the player perfectly-timed a block against drone `id`'s melee, so the
 *  server shoves that drone back along `dir` (unit, WORLD space) with impulse `force`. The server
 *  applies a decaying knockback velocity; the shoved position streams back via the normal snapshots. */
export interface DroneKnockMsg {
  t: "dknock";
  id: number;
  dir: [number, number, number];
  force: number;
}

/** A melee strike — broadcast (visual event, like FireMsg). `d` is the aim direction (unit,
 *  system space). Under SWEPT_MELEE the server re-runs the attack's BAKED BLADE ARC against
 *  rewound targets (rooms.applyMelee) and reads damage from COMBAT_ACTIONS — see
 *  docs/superpowers/specs/2026-07-14-swept-melee-action-table-design.md. */
export interface PunchMsg {
  t: "punch";
  d: [number, number, number];
  /** DEPRECATED under SWEPT_MELEE — the server reads damage from the action def, never this.
   *  Still sent for the legacy (flag-off) path and for older clients. */
  dmg?: number;
  /** Client sim-time it aimed at (server-clock ms) — lag-comp. Absent → default rewind. */
  ct?: number;
  /** CombatActionId thrown. Absent → the server falls back to the legacy cone.
   *  Untrusted client input — typed `string` on the wire deliberately; the server
   *  validates it through `combatAction()`, which returns null for anything unknown. */
  a?: string;
}

/** The enemy roster — stats live server-side, looks live client-side. The
 *  `player_*` kinds are humanoid "PLAYER" enemies: they render as a full
 *  character Avatar (not a mesh) and reuse the player's locomotion/combat. */
export const DRONE_KINDS = [
  "gunner", "scout", "heavy", "swarmer", "sniper",
  "skirmisher", "trooper", "bastion",
  "biter", "mauler", "brute",
  "player_ground", "player_flyer", "player_brawler",
  // Cache MINIBOSSES (caches phase 5b). Humanoid avatars like the `player_*`
  // kinds, but phased: their ability set swaps at HP thresholds.
  "warden", "revenant",
  // The DUELIST — a blade humanoid on the `player_brawler` body path, and the
  // first RANK-AND-FILE kind with the combat lab's in-out fencing rhythm
  // (close → orbit → telegraphed string → give ground). See ARCHETYPES.duelist.
  "duelist",
  // The RAIDER — the blade duelist's ranged opposite number, on the `player_ground`
  // body path with the arm-cannon. Between them they are the standard cache garrison:
  // weaponised humans, not drone chaff. See ARCHETYPES.raider.
  "raider",
] as const;
export type DroneKind = (typeof DRONE_KINDS)[number];

/** God-gated enemy spawn (client→server). The server ignores it unless the
 *  connection authenticated as god; placement/count are validated server-side. */
export interface DroneSpawnMsg {
  t: "dspawn";
  /** Which enemy to spawn (defaults to "gunner"). */
  kind?: DroneKind;
  /** Group size (server clamps to SPAWN_LIMITS.countMin..countMax). */
  n?: number;
  /** World placement point (required for a real spawn). */
  at?: [number, number, number];
  /** Scatter radius in metres (server clamps to SPAWN_LIMITS). */
  spread?: number;
  /** CALM stance: spawn with a tight guard leash (idle life until provoked). */
  calm?: boolean;
}

/** Client completed a mining cycle on a node; the SERVER owns depletion. */
export interface MineMsg {
  t: "mine";
  /** Node id (see shared/resources.ts systemNodes). */
  id: number;
}

/**
 * Client warped out. The departure point hosts a wormhole other pilots can
 * join for a few seconds (wormhole lifetime is client-side convention).
 */
export interface WarpMsg {
  t: "warp";
  /** Destination system id "x:y:z". */
  to: string;
  /** Departure position in system space. */
  p: [number, number, number];
}

/** Ask the server to reveal the secret content for a reachable destination. */
export interface DiscoverSystemMsg {
  t: "discover";
  system: string;
}

/** Client finished its death UX and is ready to respawn — the server restores full HP. */
export interface RespawnMsg {
  t: "respawn";
}

/** Self-inflicted damage (fall/hazard) the client reports for the server to apply to its OWN
 *  HP under SERVER_AUTHORITATIVE_HP. No cheat incentive (it's self-harm); the server clamps the
 *  amount. Hazard is sent in accumulated whole-HP chunks to bound traffic; fall is one event. */
export interface SelfDamageMsg {
  t: "selfdmg";
  n: number;
  src: "fall" | "hazard";
}

/** Convert shield into action energy (v50), at SHIELD_DRAW_RATIO loss. The
 *  server subtracts the shield and streams the result; the client grants
 *  itself the energy (energy is client-side, exactly like the heal flow). */
export interface ShieldDrawMsg {
  t: "shdraw";
  /** Shield points to draw, integer 1..30. */
  n: number;
}

export type ClientMsg =
  | JoinMsg
  | StateMsg
  | RefitMsg
  | StyleMsg
  | FireMsg
  | PunchMsg
  | WarpMsg
  | DiscoverSystemMsg
  | InvIntentMsg
  | MarketMsg
  | UpgradeMsg
  | TradeMsg
  | BoardMsg
  | DroneHitMsg
  | DroneKnockMsg
  | DroneSpawnMsg
  | MineMsg
  | RespawnMsg
  | SelfDamageMsg
  | ShieldDrawMsg
  | GodSpawnMsg
  | GodCreditsMsg
  | NpcTalkMsg
  | NpcShopMsg
  | QuestIntentMsg
  | ClaimMsg
  | ExpandMsg
  | PermitMsg
  | PlaceMsg
  | DemolishMsg
  | AbandonMsg
  | SettlementIntentMsg
  | ChatSendMsg
  | SocialMsg
  | PartyMsg
  | GuildMsg
  | CacheEnterMsg
  | CacheLeaveMsg
  | CollectionMsg;

// --- Settlement NPCs (ambient life is DERIVED client-side from seeds +
// world time — zero streaming; only the TALK round-trip touches the wire,
// because relationships are server-owned state) ---

/**
 * Talk to a settlement NPC. `npc` is the persona id
 * (`${town.seed}:npc:${i}`); the server re-derives the analytic pose and
 * validates proximity before applying relationship deltas.
 */
export interface NpcTalkMsg {
  t: "talk";
  npc: string;
  op?: "end" | "hold";
}

/** Approved NPC service intent. Prices, stock, payment, and grants are server-owned. */
export interface NpcShopMsg {
  t: "npcShop"; npc: string; seq: number; op: "list" | "buy"; offer?: string;
}
export interface NpcShopStateMsg {
  t: "npcShopState"; npc: string; seq: number; credits: number;
  offers: { id: string; itemId: string; name: string; price: number }[];
  bought?: string; err?: string;
}

/**
 * Relationship state after a talk — STATE + SALT, never dialog text: both
 * sides derive the same line from (persona, tier, salt), so there is
 * nothing spoofable on the wire.
 */
export interface NpcRelMsg {
  t: "npcRel";
  npc: string;
  /** Affinity 0..100 (drives the tier thresholds client-side too). */
  aff: number;
  tier: number;
  talks: number;
  /** Town-local days since the last conversation (dialog flavor). */
  away: number;
  salt: number;
}

// --- Quests (C3) — server-authoritative progress; boards/defs are DERIVED
// client-side from (town.seed, epoch), so only self-describing ids and the
// compact instance state travel the wire. ---

/**
 * A quest action (client → server). `q` is a self-describing quest id
 * (`${town.seed}:q:${epoch}:${i}` or `camp:<slug>`); the server re-derives
 * the def and validates proximity/epoch/inventory before mutating state.
 */
export interface QuestIntentMsg {
  t: "quest";
  seq: number;
  op: "accept" | "abandon" | "turnin" | "deliver" | "reach" | "board";
  /** Quest id (all ops except a bare board browse). */
  q?: string;
  /** deliver: target npc id (proximity-validated like talk). */
  npc?: string;
  /** board browse/accept + turnin: town seed the player claims to be at. */
  town?: string;
  /** Objective index for deliver / reach. */
  ob?: number;
}

/** Board contents for a town+epoch (server → client): the offered ids. */
export interface QuestBoardMsg {
  t: "questBoard";
  seq: number;
  town: string;
  ep: number;
  /** Offered board quest ids (client renders the defs locally). */
  q: string[];
}

/** Owner-only quest state push (server → client). */
export interface QuestStateMsg {
  t: "questState";
  seq: number;
  /** Active instances, including ones ready to turn in. */
  act: QuestInstance[];
  /** Completed quest ids (recent, capped). */
  done: string[];
}

/** Owner-only bounded authored-story consequences. */
export interface StoryStateMsg {
  t: "storyState";
  state: StoryStateV1;
}

export interface DroneState {
  id: number;
  /** Persistent encounters confirm damage via server bhit events. */
  serverHits?: boolean;
  /** Position + velocity in system space. */
  p: [number, number, number];
  v: [number, number, number];
  hp: number;
  kind?: DroneKind;
  /** HUMANOID enemies only: planet-LOCAL facing (unit forward) so the avatar can
   *  aim/strafe independent of its velocity. The client rotates it by the planet's
   *  RENDERED pose, so it tracks the spinning world without jitter. Mesh: omitted. */
  f?: [number, number, number];
  /** HUMANOID enemies only: compact animation/posture state, mirroring the
   *  PlayerState v15 keys so the same avatar driver renders both. */
  a?: DroneAnim;
  /** HUMANOID enemies only: planet-LOCAL position + planet index. The client
   *  interpolates in this time-invariant frame and converts to world via the
   *  planet's RENDERED transform (the RemoteShip anchor trick) — so a foot enemy
   *  never jitters against the rotating planet the way world-space streaming does.
   *  `p`/`v` (system space) stay for server hit validation + mesh drones. */
  lp?: [number, number, number];
  pi?: number;
}

/** Compact humanoid-enemy anim state (same key meanings as PlayerState v15). */
export interface DroneAnim {
  /** Jetpack: 1 = hover, 2 = glide. Omitted/0 = none. */
  j?: number;
  /** Aiming down sights (ranged stance). */
  am?: boolean;
  /** Unarmed-combat block (guard up). */
  bl?: boolean;
  /** Crouching. */
  cr?: boolean;
  /** Airborne (NOT grounded). */
  air?: boolean;
  /** Body-local move direction + climb (drives directional/strafe/climb clips). */
  mf?: number;
  mr?: number;
  cl?: number;
  /** Aim pitch (radians) for the torso aim solve. */
  pt?: number;
  /** Ground-relative speed (m/s) for the gait — the snapshot velocity carries
   *  planet spin, which would make a standing enemy jog in place. Enemy-only. */
  sp?: number;
  /** LIFT posture (launched/juggled body): "up" rising, "fall" dropping, "down" floored,
   *  "rise" getting up. Humanoid views play the LiftAir/KipUp clips from this; while set,
   *  `air` is also set so the client never ground-snaps a launched body. */
  lf?: "up" | "fall" | "down" | "rise";
  /** STAGGER posture (a parried attacker — Elden Ring stun): "loop" = the dizzy hang,
   *  "end" = the recover tail. Server-owned (ai/locomotion.ts stepStagger), streamed
   *  exactly like `lf`; humanoid views play st_loop/st_end from it. */
  st?: "loop" | "end";
}

export const DRONE_MAX_HP = 40;
/** Collision radius (client prediction + server hit validation). */
export const DRONE_RADIUS = 1.7;
/** Favor-the-shooter margin on the drone hurt sphere. SHARED (2026-08-02) for the same reason
 *  as PLAYER_HIT_*: the client PREDICTS bolt-vs-drone contact (white flash / flinch at the
 *  instant of impact) and the server VALIDATES it — two different volumes made hits flash
 *  without damage. The effective sphere everywhere is DRONE_RADIUS + DRONE_HIT_MARGIN, centered
 *  at the STREAMED position, every kind, no visual-scale factor. */
export const DRONE_HIT_MARGIN = DRONE_INTERP_BUFFERED ? 0.25 : 0.5;

// --- Server → Client ---

export interface RemotePlayer {
  id: number;
  name: string;
  state: PlayerState;
  ship: ShipLayout;
  style: CharacterStyle;
  /** Guild tag, rendered as `[TAG] Name` over their head. Rides the join
   *  payload so everyone already in the room — and every later joiner via
   *  `welcome` — sees it with no extra traffic. */
  tag?: string;
}

export interface WelcomeMsg {
  t: "welcome";
  id: number;
  players: RemotePlayer[];
  /** Server-verified god-mode privilege for THIS player (item spawner UI).
   *  Derived only from the verified login — never from a client claim. */
  god?: boolean;
  /** True iff the SERVER verified this connection's login (uid resolved). Absent =
   *  seated as a GUEST: the account echo is a blank guest inventory and nothing
   *  persists server-side. The client MUST NOT adopt inventory echoes from a guest
   *  seat while it believes it is logged in — doing so wiped worn armor (the blank
   *  echo replaced the restored bag, then the periodic cloud save persisted it). */
  authed?: boolean;
  /** One-time token for the UNRELIABLE (geckos.io UDP) channel: the client presents it as the
   *  channel authorization so the server can bind the datachannel to this player. Present only
   *  when the server runs with UDP enabled (TRANSPORT_UNRELIABLE + RT_UDP=1). */
  rt?: string;
}

export interface PlayerJoinedMsg {
  t: "joined";
  player: RemotePlayer;
}

export interface PlayerLeftMsg {
  t: "left";
  id: number;
}

export interface StatesMsg {
  t: "states";
  /** Server timestamp (ms) for interpolation buffering. */
  time: number;
  states: { id: number; s: PlayerState; at?: number }[];
}

/** A player in the room changed their ship layout. */
export interface PlayerRefitMsg {
  t: "refit";
  id: number;
  ship: ShipLayout;
}

/** A player in the room restyled their character. */
export interface PlayerStyleMsg {
  t: "style";
  id: number;
  style: CharacterStyle;
}

/** A player's guild tag changed mid-session (founded/joined/left/disbanded) —
 *  the room re-bakes their name sprite. Empty string = no guild. */
export interface PlayerTagMsg {
  t: "ptag";
  id: number;
  tag: string;
}

/** A player in the room fired a weapon group. */
export interface PlayerFireMsg {
  t: "fire";
  id: number;
  g: 1 | 2;
  /** Aim direction for on-foot shots (see FireMsg.d). */
  d?: [number, number, number];
  /** Vehicle socket fired, so observers emit from one physical mount. */
  vh?: string;
}

/** Relayed melee swing (server→others): play `id`'s swing. Damage is NOT applied from this —
 *  the server resolves melee itself (rooms.applyMelee) and streams the result as `bhit`/`hp`. */
export interface PlayerPunchMsg {
  t: "punch";
  id: number;
  d: [number, number, number];
  /** Damage of the move thrown (P1 per-move). Absent = fists default (back-compat). */
  dmg?: number;
  /** CombatActionId the attacker threw (v23), relayed from PunchMsg.a so OBSERVERS play the
   *  right swing instead of a generic jab. Absent = an old client / the fists fallback. */
  a?: string;
}

/** Drone snapshot broadcast (10 Hz, same cadence as player states). */
export interface DronesMsg {
  t: "drones";
  time: number;
  list: DroneState[];
}

/** A drone fired: all clients render the bolt; the target applies damage. */
export interface DroneFireMsg {
  t: "dfire";
  id: number;
  /** Muzzle position + direction, system space. */
  p: [number, number, number];
  d: [number, number, number];
  /** Player id the shot is aimed at (that client owns the damage). */
  target: number;
  /** Damage + bolt speed (per-kind; clients default 8 / 130). */
  dmg?: number;
  bs?: number;
}

/** A melee enemy struck: all clients play the lunge on drone `id`; the local
 *  char self-applies `dmg` if within `reach` (relay model, like player punches). */
export interface DroneMeleeMsg {
  t: "dmelee";
  id: number;
  /** Aimed-at player (server bookkeeping; clients damage by range, not id). */
  target: number;
  /** Strike origin + direction, world space. */
  p: [number, number, number];
  d: [number, number, number];
  dmg: number;
  /** Contact reach (m) the client range-tests against. */
  reach: number;
  /** HUMANOID brawlers: which combat move to play (a MoveId — jab/hook/low_kick/
   *  mid_kick/high_kick). Absent → mesh drones play their generic lunge. */
  move?: string;
  /** TELEGRAPH: ms from this event until the strike LANDS. The server schedules the
   *  damage at emit+wu and RE-CHECKS reach (and the victim's i-frames) at impact — so
   *  the windup is a window the player can genuinely dodge or back out of, instead of
   *  damage landing the same tick the AI decided to swing. Absent/0 → legacy instant. */
  wu?: number;
}

/** A one-shot avatar action for a HUMANOID enemy (relay, like dfire/dmelee):
 *  all clients play it on drone `id`. Currently just dodge-rolls; extensible. */
export interface DroneActMsg {
  t: "dact";
  id: number;
  /** Action kind. "evade" = dodge-roll. */
  a: "evade";
  /** Roll direction (forward/back/left/right). */
  d: "f" | "b" | "l" | "r";
}

/** A drone died (visual: explosion; the killer gets a LootMsg). */
export interface DroneDeathMsg {
  t: "ddeath";
  id: number;
  p: [number, number, number];
}

/**
 * Authoritative projectile impact (server→room). The server-simulated bolt fired by
 * player `by` struck target `id` — a drone now, a player (`k:"char"`) in Phase 2 — for
 * `dmg` at world-space point `p`. Drives damage numbers + impact FX (and, in P2, the
 * victim's server-owned HP). Drone HP still streams via `drones`; a kill still emits
 * `ddeath`. Replaces the client `dhit` claim as the source of hit truth.
 */
export interface BoltHitMsg {
  t: "bhit";
  by: number;
  k: "drone" | "char";
  id: number;
  dmg: number;
  p: [number, number, number];
}

/** Server grants items (drone salvage; later: extraction rewards). */
export interface LootMsg {
  t: "loot";
  items: { id: string; n: number }[];
}

// --- Commodity market (v11) ---

/** Trade or quote request; the server validates docking proximity. */
export interface MarketMsg {
  t: "market";
  seq: number;
  op: "quote" | "buy" | "sell";
  /** Item id for buy/sell. */
  id?: string;
  n?: number;
}

/** Station quote sheet for THIS system (owner-only reply). */
export interface MarketStateMsg {
  t: "marketState";
  seq: number;
  /** Per-good {sell, buy} in credits — sell = what YOU pay. */
  prices: Record<string, { sell: number; buy: number }>;
  /** Set on a rejected trade (toast text). */
  err?: string;
}

// --- Combat upgrades (v22) ---

/** Buy the next level in one of the four energy tracks. The server owns credits and
 *  levels; it validates the cost and answers with the account echo (`inv`). */
export interface UpgradeMsg {
  t: "upgrade";
  seq: number;
  track: string;
}

// --- Market board: listings & auctions (v13) ---

/** A live (or claimable) board entry, as sent to clients. */
export interface BoardListing {
  id: string;
  kind: "listing" | "auction";
  item: string;
  n: number;
  /** Fixed price (listing) or buyout if present (auction, optional). */
  price?: number;
  /** Auction: current high bid + minimum next bid. */
  bid?: number;
  minBid?: number;
  bidderName?: string;
  sellerName: string;
  /** Epoch ms. */
  expiresAt: number;
  /** True when this entry is YOURS (cancel) / your claim (claim). */
  mine?: boolean;
  claimable?: boolean;
}

export interface BoardMsg {
  t: "board";
  seq: number;
  op: "browse" | "list" | "bid" | "buyout" | "cancel" | "claim";
  /** Listing id for bid/buyout/cancel/claim. */
  id?: string;
  /** For op "list". */
  item?: string;
  n?: number;
  price?: number;
  /** "list": auction when set (starting bid); fixed listing otherwise. */
  minBid?: number;
  durationS?: number;
}

export interface BoardStateMsg {
  t: "boardState";
  seq: number;
  listings: BoardListing[];
  err?: string;
}

// --- Escrowed player-to-player trade (v12) ---

/** One side's staged offer. */
export interface TradeOffer {
  items: { id: string; n: number }[];
  credits: number;
}

/**
 * Trade flow: invite → accept → (offer/confirm)* → both confirmed →
 * ATOMIC server-side swap. Any offer change clears both confirmations.
 * Nothing is locked while staging — the swap re-validates everything and
 * applies all-or-nothing, so a mid-trade spend just fails the trade.
 */
export interface TradeMsg {
  t: "trade";
  op: "invite" | "accept" | "decline" | "offer" | "confirm" | "cancel";
  /** Player id (invite target). */
  target?: number;
  offer?: TradeOffer;
}

export interface TradeStateMsg {
  t: "tradeState";
  phase: "invite" | "open" | "done" | "cancelled";
  /** Counterparty (invites + the open window header). */
  peer?: { id: number; name: string };
  you?: TradeOffer;
  them?: TradeOffer;
  youConfirmed?: boolean;
  themConfirmed?: boolean;
  err?: string;
}

// --- Server-authoritative inventory (v10) ---

/**
 * Client inventory INTENT. The client applies the mutation optimistically
 * for latency-free UI and the server replies with the authoritative state
 * (InvStateMsg) echoing `seq` — the only path that makes items real.
 */
export interface InvIntentMsg {
  t: "inv";
  /** Client-increasing sequence; echoed by the authoritative ack. */
  seq: number;
  op: "move" | "use" | "craft" | "equip" | "spend"
    /** v49: wear the gear/weapon at bag slot `a` — it LEAVES the bag. */
    | "equipGear"
    /** v49: take off equip slot `eq`, returning it to the bag soulbound. */
    | "unequip";
  /** Slot index: move-from / use-at / equip-from / equipGear-from. */
  a?: number;
  /** Slot index: move-to. */
  b?: number;
  /** Equip target: legacy "primary"/"secondary" for op "equip", or an EquipSlot
   *  (the eight StyleParts plus "mainhand"/"offhand") for op "unequip". */
  eq?: "primary" | "secondary" | EquipSlot;
  /** Recipe OUTPUT item id for op "craft". */
  recipe?: string;
  /** Item id for op "spend" (consume-by-id, e.g. a fuel cell at jump). For op "equip":
   *  a STANDARD_WEAPONS id (or "none" to unequip) — the id form wins over the slot form. */
  id?: string;
  /** Count for op "spend" (default 1). */
  n?: number;
}

/**
 * God-mode item spawn request (client->server). Grants `n` of item `id` into
 * the requester's own inventory. The server IGNORES this unless the connection
 * is server-verified as god (the client UI is gated too, but the server is the
 * only authority — a forged godSpawn from a non-god connection is a no-op).
 */
export interface GodSpawnMsg {
  t: "godSpawn";
  id: string;
  n: number;
}

/**
 * God-mode credit grant (client->server). Adds `amount` credits to the
 * requester's own balance (negative removes). Like `godSpawn`, the server is
 * the only authority — a forged godCredits from a non-god connection is a no-op,
 * and the server clamps the magnitude and refuses to drive the balance negative.
 */
export interface GodCreditsMsg {
  t: "godCredits";
  amount: number;
}

/**
 * Garage + wardrobe intents that do NOT touch the bag (v49). Bag-mutating equipment
 * ops ride InvIntentMsg and ack through InvStateMsg; these ack through
 * CollectionStateMsg. Same shape as SocialMsg/SettlementIntentMsg: seq + op union.
 */
export interface CollectionMsg {
  t: "collection";
  /** Client-increasing sequence; echoed by the authoritative ack. */
  seq: number;
  op: "vehicle" | "refresh";
  /** For op "vehicle": the VEHICLES id to make active, or null to stow. */
  id?: string | null;
}

/**
 * The authoritative collection for THIS character (owner-only, v49).
 *
 * The server sends the UNION of STARTER_UNLOCKS and the persisted ledger, so the
 * client never has to know which grants are implicit.
 */
export interface CollectionStateMsg {
  t: "collectionState";
  /** Last applied intent seq (0 for the join push / server-side grants). */
  seq: number;
  /** Wardrobe tokens, "<part>:<variant>". */
  gear: string[];
  /** Garage vehicle ids — the "veh:" prefix is already stripped. */
  vehicles: string[];
  /** Active garage vehicle id, or null. */
  vehicle: string | null;
  /** Set when an intent was refused (toast text). */
  err?: string;
}

/** Authoritative inventory + credits for THIS player (owner-only). */
export interface InvStateMsg {
  t: "invState";
  /** Last applied intent seq (0 for the join push / server-side grants). */
  seq: number;
  inventory: unknown; // InventoryState — validated client-side on receipt
  credits: number;
  fuelKit: boolean;
  /** UpgradeState — the four combat-energy tracks. Server-owned, validated on receipt. */
  upgrades?: unknown;
  /** Set when an account-affecting action was refused (toast text): an `upgrade`
   *  purchase, or a v49 garage claim ("ALREADY IN GARAGE"). */
  err?: string;
}

/** Node depletion snapshot/delta: remaining units per node id. */
export interface NodesMsg {
  t: "nodes";
  list: { id: number; left: number }[];
}

/** A mining claim was honored: the claimant's grant (room sees "nodes"). */
export interface MinedMsg {
  t: "mined";
  id: number;
  item: string;
  n: number;
  left: number;
}

/** A player in the room warped out, leaving a joinable wormhole. */
export interface PlayerWarpMsg {
  t: "warp";
  id: number;
  to: string;
  p: [number, number, number];
}

/**
 * Authoritative player health for the OWNER (server owns HP under SERVER_AUTHORITATIVE_HP).
 * Streamed on any meaningful change — damage, heal, regen, respawn. `dead` marks the kill so
 * the client plays its death UX and then sends `respawn`.
 */
export interface HealthMsg {
  t: "hp";
  hp: number;
  max: number;
  dead?: boolean;
  /** Personal shield (v50): current + capacity. Absent = no shield worn. */
  sh?: number;
  shMax?: number;
}

export interface ErrorMsg {
  t: "error";
  message: string;
}

/** Key release result. Ciphertext remains public/cacheable; this small
 *  authenticated message is the discovery boundary. */
export type SystemRevealMsg =
  | { t: "systemReveal"; systemId: string; reveal: SystemRevealV1 }
  | { t: "systemReveal"; systemId: string; error: string };

// --- Homesteads: player land ownership + building (v26) ---
//
// Beacon CLAIM/EXPAND/PERMIT are seq-acked intents (the InvIntent/InvState idiom):
// the client applies optimistically and the server replies with authoritative
// homestead state echoing `seq` — the only path that makes a claim real. Piece
// place/demolish (Phase 2) and terraform (Phase 3) extend this block later.

/** Plant a claim beacon. The server validates ownership, proximity (the player must
 *  stand near `anchor` on `planetId`, re-derived server-side), economy (credits), and
 *  the claim rules (caps / overlap / town) before it becomes real. */
export interface ClaimMsg {
  t: "claim";
  /** Client-increasing sequence; echoed by the authoritative ack. */
  seq: number;
  /** Stable PlanetDef.id the beacon is planted on. */
  planetId: string;
  /** Desired anchor, planet-local metres (proximity-gated against the player's pose). */
  anchor: [number, number, number];
  /** Desired starting tier (default 0). */
  tier?: number;
}

/** Grow an owned beacon to a higher tier (pays the credit difference). */
export interface ExpandMsg {
  t: "expand";
  seq: number;
  beaconId: string;
  /** Target tier (must exceed the current tier). */
  tier: number;
}

/** Add/remove a member on an owned beacon's build/edit allowlist. Target by
 *  `uid` (allowlist rows) OR by `player` (a room player id — the SERVER
 *  resolves the uid; clients never learn each other's uids). One is required. */
export interface PermitMsg {
  t: "permit";
  seq: number;
  beaconId: string;
  op: "add" | "remove";
  uid?: string;
  player?: number;
}

/** Place one building piece inside an owned/permitted beacon (v27). The server
 *  validates build permission, containment (pointInBeacon), the piece budget,
 *  and the material cost before it becomes real. */
export interface PlaceMsg {
  t: "place";
  seq: number;
  beaconId: string;
  /** Piece — { t: catalog index, p, q (planet-local), mat }; validated server-side. */
  piece: unknown;
}

/** Remove a placed piece by its server-minted pid (refunds a material fraction). */
export interface DemolishMsg {
  t: "demolish";
  seq: number;
  beaconId: string;
  pid: number;
}

/** Abandon an owned claim outright. OWNER-only (allowlist grants build, not
 *  this), refused while pieces remain (demolish first — no silent mass loss),
 *  and the deed's credits are NOT refunded (land is a sink by design). */
export interface AbandonMsg {
  t: "abandon";
  seq: number;
  beaconId: string;
}

/**
 * Authoritative homestead state for the player's CURRENT system (owner-only push).
 * Carries every beacon in the system the client needs to render (its own + others';
 * a public system is capped, a home system is single-occupant). `seq` echoes the
 * triggering intent (0 for the join push); `err` is set when an intent was refused.
 */
export interface HomesteadStateMsg {
  t: "hsState";
  seq: number;
  /** Beacon[] — validated client-side on receipt (like invState.inventory). */
  beacons: unknown[];
  /** Placed pieces per beacon id (PlacedPiece[] — validated on receipt). Nested
   *  arrays are fine on the JSON wire; only Firestore storage stringifies. */
  pieces?: Record<string, unknown[]>;
  /** The OWNER'S beacons across ALL systems (Beacon[] — the "my homesteads"
   *  roster + warp targets). Present only for signed-in players. */
  mine?: unknown[];
  err?: string;
}

/** A single homestead change, broadcast to everyone in the room so the world
 *  stays live: beacon add/update/remove, or a piece placed/removed within one. */
export interface HomesteadDeltaMsg {
  t: "hsDelta";
  op: "add" | "update" | "remove" | "piece-add" | "piece-remove";
  /** Present for beacon add/update (a Beacon, validated on receipt). */
  beacon?: unknown;
  /** Present for beacon remove AND for both piece ops (the owning beacon). */
  beaconId?: string;
  /** Present for piece-add (a PlacedPiece, validated on receipt). */
  piece?: unknown;
  /** Present for piece-remove: the demolished piece. */
  pid?: number;
  /** piece-remove: the FULL removal set when the demolish cascaded (the target
   *  plus every piece that would have floated). Absent → just `pid`. */
  pids?: number[];
}

// --- Player settlements: offline finite production (v42) ---

/** Owner-only management intent for one claimed settlement. All item counts,
 * room eligibility, workers, recipes, and elapsed work are revalidated by the
 * server; the client only asks. */
export interface SettlementIntentMsg {
  t: "settlement";
  seq: number;
  beaconId: string;
  op: "state" | "bootstrap" | "deposit" | "withdraw" | "queue" | "upgradeFacility"
    | "setTarget"
    | "promoteResident" | "recruit" | "reassignResident" | "relicenseFacility"
    | "advanceStage" | "ack" | "decommission" | "storeBuy"
    | "foundBank" | "bankDeposit" | "bankWithdraw"
    | "guildPin" | "guildContribute" | "guildComplete" | "guildAbandon";
  item?: string;
  n?: number;
  facilityId?: string;
  residentId?: string;
  applicantId?: string;
  applicantProfession?: string;
  /** promoteResident: optional specialty chosen or changed with the rank. */
  specialty?: string;
  /** storeBuy: the deterministic storefront offer id. */
  offerId?: string;
  homePid?: number;
  workplacePid?: number;
  targetAnchorPid?: number;
  recipeId?: string;
  workerIds?: string[];
  targetQuantity?: number;
  eventIds?: string[];
}

/** Seq-acked authoritative settlement snapshot. `state` is validated before
 * client use, as with the inventory and homestead payloads. */
export interface SettlementStateMsg {
  t: "settlementState";
  seq: number;
  beaconId: string;
  state?: unknown;
  err?: string;
}

/** Public inhabitant projection for world rendering. It deliberately excludes
 * storage, inputs, completion receipts, and owner ids. `full` replaces the
 * current-system set on join; false updates only the listed beacon records. */
export interface SettlementPublicMsg {
  t: "settlementPublic";
  full: boolean;
  settlements: unknown[];
}

// --- Social: chat (v28) ---
//
// Chat is a RELAY, not state: the server sanitizes (sanitizeChatText), stamps
// the sender name from the session's character record (a client can never
// speak as someone else), runs the moderation hook, and fans out per channel.
// Nothing is persisted — clients keep their own scrollback. Friends/party/
// guild ops extend this block in later phases (see the social design spec).

/** Send one chat line. `ch` routes it: "local" = the sender's system room;
 *  "party"/"guild" = those member sets (cross-system); "direct" = a whisper
 *  to friend `to` (a character NAME — uids never travel to clients). Free
 *  text is HOSTILE INPUT: the server strips control/zero-width chars and
 *  caps at CHAT_MAX before relay; clients render it via textContent ONLY. */
export interface ChatSendMsg {
  t: "chat";
  ch: ChatChannel;
  text: string;
  /** direct only: target character name. */
  to?: string;
}

/** A relayed chat line (server → client). `from` is ALWAYS server-stamped
 *  from the sender's character record, never echoed from the client. */
export interface ChatMsg {
  t: "chatMsg";
  ch: ChatChannel;
  from: string;
  /** direct: the whisper target, echoed so the sender renders "to NAME". */
  to?: string;
  text: string;
  /** Server timestamp (ms). */
  at: number;
  /** Private notice line (rate limit, offline target) — not from a player. */
  err?: boolean;
}

// --- Social: friends (Phase 2, v37) ---

/** A friends-list op. Targets are character NAMES — uids never travel on the
 *  wire in either direction; the server resolves names via the characterNames
 *  registry (and its presence map for online players). */
export interface SocialMsg {
  t: "social";
  /** Client op counter, echoed on the acking socialState. */
  seq: number;
  op: "request" | "accept" | "decline" | "remove" | "block" | "unblock" | "privacy";
  /** Target character name (all ops except privacy). */
  name?: string;
  /** privacy: the new shareSystem value. */
  on?: boolean;
}

/** One friend row as the client may see it. `system` only rides when the
 *  friend is online AND shares their location (their shareSystem). */
export interface SocialFriendWire {
  name: string;
  /** Edge creation time (server ms) — stable sort key for the panel. */
  at: number;
  online?: boolean;
  system?: string;
}

/** Seq-acked FULL social state (quest-state precedent): pushed on login, on
 *  every accepted/refused op (err carries the refusal), and whenever a
 *  friend's presence changes. Small by construction (caps ≤ 100/40/40/100). */
export interface SocialStateMsg {
  t: "socialState";
  /** Echo of the triggering op's seq; 0 for server-initiated pushes. */
  seq: number;
  friends: SocialFriendWire[];
  /** Requests awaiting MY answer / requests I sent (names + created-at). */
  incoming: { name: string; at: number }[];
  outgoing: { name: string; at: number }[];
  blocked: { name: string; at: number }[];
  shareSystem: boolean;
  /** Refusal notice for the acked op (HUD-toast style, uppercase). */
  err?: string;
}

// --- Social: parties (Phase 3, v38) ---

/** A party op. Targets are a room player id (someone co-present, the trade/
 *  permit idiom) or a character NAME — uids never travel in either direction. */
export interface PartyMsg {
  t: "party";
  /** Client op counter, echoed on the acking partyState. */
  seq: number;
  op: "invite" | "accept" | "decline" | "leave" | "kick" | "promote" | "disband" | "rally";
  /** invite: room player id of a pilot in MY system (preferred — no name typing). */
  player?: number;
  /** invite by name (online only) / kick / promote target. */
  name?: string;
}

/** One party row. Vitals ride the 1 Hz tick; `id` only when that member is in
 *  the VIEWER's system (it's what tints their marker). */
export interface PartyMemberWire {
  name: string;
  leader?: boolean;
  /** False while inside the disconnect grace (a warp reads as online). */
  online?: boolean;
  /** Where they are — parties see each other's system, privacy is a friends
   *  concept (you can always leave the party). */
  system?: string;
  /** Room player id, only when co-present with the viewer. */
  id?: number;
  hp?: number;
  maxHp?: number;
  /** The viewer's own row. */
  me?: boolean;
}

/** Full party state (socialState precedent): pushed on every change, on the
 *  1 Hz vitals tick, and as the ack of an op. `members: []` = not in a party. */
export interface PartyStateMsg {
  t: "partyState";
  /** Echo of the triggering op's seq; 0 for server-initiated pushes. */
  seq: number;
  members: PartyMemberWire[];
  /** An invite awaiting MY answer. `ttl` is ms remaining (never an absolute
   *  server clock — client clocks disagree). */
  invite?: { from: string; ttl: number };
  /** A party member just warped — fuel for the "F — FOLLOW <name>" prompt. */
  jump?: { name: string; to: string };
  /** The party's rally point: a system every member's chart marks. Any member
   *  can set it (to the system they're standing in) or clear it. */
  rally?: { system: string; by: string };
  /** Refusal notice for the acked op (HUD-toast style, uppercase). */
  err?: string;
}

// --- Social: guilds (Phase 4, v39) ---

/** A guild op. Targets are a room player id or a character NAME — uids never
 *  travel. `create` carries the guild's name + its 2–4 char tag. */
export interface GuildMsg {
  t: "guild";
  /** Client op counter, echoed on the acking guildState. */
  seq: number;
  op: "create" | "invite" | "accept" | "decline" | "leave" | "kick" | "promote" | "demote" | "disband" | "motd";
  /** create: the guild name / kick+promote+demote+invite: the target pilot. */
  name?: string;
  /** create: the over-head tag. */
  tag?: string;
  /** motd: the new message of the day (free text — server-sanitized). */
  motd?: string;
  /** invite: room player id of a pilot in MY system. */
  player?: number;
}

/** One roster row. Presence rides the same push (guild rosters are small). */
export interface GuildMemberWire {
  name: string;
  role: "leader" | "officer" | "member";
  /** The viewer's own row (the panel hides self-targeting actions). */
  me?: boolean;
  online?: boolean;
  /** Where they are, when online — a guild is a public-ish club, not a party;
   *  the friends privacy toggle doesn't apply to the roster. */
  system?: string;
}

/** Full guild state (socialState precedent): pushed on login, on every op, and
 *  whenever a guildmate's membership or presence changes. */
export interface GuildStateMsg {
  t: "guildState";
  /** Echo of the triggering op's seq; 0 for server-initiated pushes. */
  seq: number;
  /** Absent = not in a guild. */
  guild?: {
    name: string;
    tag: string;
    /** The VIEWER's role — what the panel gates its buttons on. */
    role: "leader" | "officer" | "member";
    /** Officer-settable message of the day; toasted once, at login. */
    motd?: string;
    members: GuildMemberWire[];
  };
  /** An invite awaiting my answer (`ttl` is ms remaining, never a clock). */
  invite?: { from: string; guild: string; tag: string; ttl: number };
  /** Refusal notice for the acked op (HUD-toast style, uppercase). */
  err?: string;
  /** One-off news the player has to be told (login reconcile: the guild was
   *  disbanded, or they were removed while offline). */
  notice?: string;
}

// --- Caches: instanced dungeons (Phase 3, v40) ---

/**
 * "I am standing at this aperture — open my instance" (client → server).
 *
 * `site` is a CLAIM: the server re-derives the site and (from Phase 6) checks
 * proximity before honouring it. The instance KEY is never on the wire — the
 * server reads party membership itself.
 */
export interface CacheEnterMsg {
  t: "cacheEnter";
  /** Site token — planet id + site index, no ':' (see cacheSystemId). */
  site: string;
  /** 0 = short run, 1 = long run. A site property, echoed back validated. */
  tier: number;
}

/** "I'm done with this instance" (client → server). The client then simply
 *  joins its origin system again; this is the server's cue to start the
 *  reconnect grace on the instance. */
export interface CacheLeaveMsg {
  t: "cacheLeave";
}

/**
 * The instance the caller may now join, plus its live progress (server →
 * client).
 *
 * NEVER geometry: it carries the four REGENERATION INPUTS (site, epoch, theme,
 * tier) and both sides call the same `shared/src/cache` generator, so client
 * and server hold byte-identical layouts without a single collider on the wire.
 */
export interface CacheStateMsg {
  t: "cacheState";
  /** Canonical system id `cache:<site>:<key>:<epoch>` — join THIS to enter. */
  instance: string;
  site: string;
  epoch: number;
  theme: string;
  tier: number;
  /** Server-owned run progress. */
  prog: {
    /** Indices of the layout's opened gate edges. */
    gates: number[];
    /** Room indices of the keys the party has picked up. */
    keys: number[];
    bossAlive: boolean;
  };
}

/**
 * CONTAINMENT — the cache ENVELOPE RULE's per-player traffic (server → client).
 *
 * ⚠ PER-PLAYER, WHICH IS WHY IT IS NOT A FIELD ON `CacheStateMsg`. `cacheState` is a
 * ROOM BROADCAST of run progress; whether *you* are near the bound is yours alone, and
 * folding it into a broadcast would either leak one player's position to the room or
 * fan the broadcast out per socket. This is additive on the server→client union, which
 * has no allowlist (`parseServerMsg` accepts any tagged frame) — so no
 * `PROTOCOL_VERSION` bump: an older client ignores it exactly as it ignores any tag it
 * has no case for, and the only thing it loses is the warning.
 *
 * Sent on EDGES only — entering/leaving the warn band, and each relocation.
 */
export interface CacheEnvMsg {
  t: "cacheEnv";
  /** True while inside the warn band, false when the player is clear again. */
  warn: boolean;
  /** Present ONLY on a relocation: the PAD-LOCAL (x, z) the server has put you at
   *  (the entrance room's centre). The client seats itself on its own ground there —
   *  it knows its dressing's floor height and the server deliberately does not. */
  to?: [number, number];
}

/** Recent LOCAL scrollback for the system just joined/warped into (server →
 *  client) — the room's last lines so chat isn't empty at login. Same
 *  server-stamped ChatMsg shapes; clients render them dimmed as history and
 *  never re-bubble them (they are scrollback, not live speech). */
export interface ChatHistMsg {
  t: "chatHist";
  lines: ChatMsg[];
}

export type ServerMsg =
  | WelcomeMsg
  | PlayerJoinedMsg
  | PlayerLeftMsg
  | StatesMsg
  | PlayerRefitMsg
  | PlayerStyleMsg
  | PlayerFireMsg
  | PlayerPunchMsg
  | PlayerWarpMsg
  | DronesMsg
  | import('./wildlife.js').WildlifeMsg
  | DroneFireMsg
  | DroneMeleeMsg
  | DroneActMsg
  | DroneDeathMsg
  | BoltHitMsg
  | HealthMsg
  | LootMsg
  | NodesMsg
  | MinedMsg
  | InvStateMsg
  | MarketStateMsg
  | TradeStateMsg
  | BoardStateMsg
  | NpcRelMsg
  | NpcShopStateMsg
  | QuestBoardMsg
  | QuestStateMsg
  | StoryStateMsg
  | HomesteadStateMsg
  | HomesteadDeltaMsg
  | SettlementStateMsg
  | SettlementPublicMsg
  | ChatMsg
  | ChatHistMsg
  | SocialStateMsg
  | PartyStateMsg
  | GuildStateMsg
  | CacheStateMsg
  | CacheEnvMsg
  | PlayerTagMsg
  | ErrorMsg
  | SystemRevealMsg
  | CollectionStateMsg;

/** Star-system / galaxy id: integer lattice coordinates "x:y:z". */
export function parseSystemId(id: unknown): [number, number, number] | null {
  if (typeof id !== "string" || !/^-?\d{1,4}:-?\d{1,4}:-?\d{1,4}$/.test(id)) return null;
  const [x, y, z] = id.split(":").map(Number);
  return [x, y, z];
}

export function parseClientMsg(raw: string): ClientMsg | null {
  try {
    const msg = JSON.parse(raw);
    // ⚠ EVERY ClientMsg `t` must be listed here or the server SILENTLY
    // drops it ("punch" was missing for a while — unreachable handler).
    const types = ["join", "state", "refit", "style", "fire", "punch", "warp", "discover", "dhit", "dknock", "dspawn", "mine", "inv", "market", "trade", "board", "upgrade", "godSpawn", "godCredits", "respawn", "selfdmg", "talk", "npcShop", "quest", "claim", "expand", "permit", "place", "demolish", "abandon", "settlement", "chat", "social", "party", "guild", "cacheEnter", "cacheLeave", "collection", "shdraw"];
    if (msg && types.includes(msg.t)) return msg as ClientMsg;
    return null;
  } catch {
    return null;
  }
}

export function parseServerMsg(raw: string): ServerMsg | null {
  try {
    const msg = JSON.parse(raw);
    if (msg && typeof msg.t === "string") return msg as ServerMsg;
    return null;
  } catch {
    return null;
  }
}
